// @noflow
import initI18n from '@/packs/localisation'
import type { Language } from '@/packs/localisation'
import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import 'intersection-observer'
import React, { useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import 'scroll-behavior-polyfill'

import { DiscountBasis } from '@/utils/butternutbox/discountCodes'
import { initGoogleClickIdentifierSession } from '@/utils/googleClickIdentifierSession'
import { isDevelopment } from '@/utils/isDevelopment'
import { playVideosOnScroll } from '@/utils/playVideosOnScroll'
import * as Sentry from '@/utils/sentry'

import BREAKPOINTS from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import segmentTrack from '@/components/analytics/Analytics'
import { initCookieConsent } from '@/components/cookie_consent'
import LearnMoreSection from '@/components/shared/LearnMoreAboutButternut'
import TabbedComponent from '@/components/shared/TabbedComponent'
import BreedQuestionaireCard from '@/components/shared/elements/BreedQuestionaireCard/BreedQuestionaireCard'
import ComparisonTable from '@/components/shared/elements/ComparisonTable/ComparisonTable'
import { initFooterLeadCapture } from '@/components/shared/elements/FooterLeadCapture/FooterLeadCapture'
import RealStoriesVideoCarousel from '@/components/shared/elements/RealStoriesVideoCarousel/RealStoriesVideoCarousel'
import FullSectionRoughCostCalculator from '@/components/shared/elements/RoughCostCalculator/FullSectionRoughCostCalculator'
import Cta from '@/components/shared/elements/RoughCostCalculator/components/CTA'
import DogDetailsForm from '@/components/shared/elements/RoughCostCalculator/components/DogDetailsForm'
import Plan from '@/components/shared/elements/RoughCostCalculator/components/Plan'
import SocialReviewsSection from '@/components/shared/elements/SocialReviewsSection/SocialReviewsSection'
import TestimonialsTrustpilot from '@/components/shared/elements/TestimonialsTrustpilot/TestimonialsTrustpilot'
import TrustpilotWidget, {
  Theme,
  Variant
} from '@/components/shared/elements/TrustpilotWidget/TrustpilotWidget'
import Video from '@/components/shared/elements/Video/Video'
import initGeoIpWidget from '@/components/shared/geo_ip_widget/initGeoIpWidget'

import type { Dog } from '@/shared_types/rails_models/dogs'
import type { Code as CountryCode } from '@/shared_types/rails_models/shipping_countries'

const { IntersectionObserver } = global

const namespace = 'homepage'
const screenIdentifier = 'pt_page'

const DOG_PERSISTENCE_KEY = 'new-wizard-dog-persistence-v1'
const empty_dog_data = [
  {
    name: null,
    sex: null,
    deSexed: null,
    age: null,
    broughtHome: null,
    breed: null,
    currentlyEating: null,
    fussiness: null,
    bodyCondition: null,
    mass: null,
    activityLevel: null,
    isWorkingDog: null,
    allergies: null,
    healthIssues: null,
    snackingHabits: null
  }
]

const getPersistedDogData = (): Dog[] => {
  try {
    const raw = window.localStorage.getItem(DOG_PERSISTENCE_KEY)
    if (raw) {
      return JSON.parse(raw)
    } else {
      return []
    }
  } catch (_) {
    window.localStorage.removeItem(DOG_PERSISTENCE_KEY)
    return []
  }
}

const setPersistedDogData = (dogData: Array<Dog>, value: string): void => {
  if (dogData && dogData.length > 0) {
    dogData[0].name = value
    window.localStorage.setItem(DOG_PERSISTENCE_KEY, JSON.stringify(dogData))
  } else {
    window.localStorage.setItem(
      DOG_PERSISTENCE_KEY,
      JSON.stringify(empty_dog_data)
    )
  }
}

const triggerDogNameInputAnalytics = (): void => {
  const eventName = 'Wizard Entry Typed Dog Name'
  const properties = {
    path: window.location.pathname
  }
  segmentTrack(eventName, properties)
}

const dataContainer = document.getElementById(
  'paid-traffic-content-tag'
) as HTMLElement
if (!dataContainer) {
  Sentry.captureException('Could not find #paid-traffic-content-tag', {
    tags: {
      product: Sentry.Product.PaidTraffic
    }
  })
}

const { dataset } = dataContainer

const {
  isPrewizardGuest,
  language,
  showExtras,
  shippingCountryCode,
  discountBasis,
  discountFirstPartValue,
  trustpilotBusinessUnitId,
  isPsibufet,
  isOg,
  signedIn,
  phase3MultivariateVariation
} = dataset

const preferredLanguage = (language as Language) || 'en'
const countryCode = (shippingCountryCode as CountryCode) || 'GB'
const amplitudeApiKey = process.env.AMPLITUDE_API_KEY

const sessionReplayTracking = sessionReplayPlugin({
  debugMode: isDevelopment(),
  sampleRate: 0.2
})
amplitude.add(sessionReplayTracking)

const initBreedSelector = () => {
  const breedSelectorElement = document.getElementById('breed-selector')
  const discountBanner = document.getElementsByClassName('discount-banner')[0]

  // We return null for signed in users who we don't show the breed selector
  if (!breedSelectorElement) return null

  const ctaDestination =
    isPrewizardGuest === 'true' ? '/wizard/new' : '/plans/recipes'

  const BreedSelectorComponent = () => {
    const { windowWidth } = useWindowSize()

    return (
      <BreedQuestionaireCard
        ctaDestination={ctaDestination}
        isPreWizardGuest={isPrewizardGuest === 'true'}
        scrollToOnFocus={
          windowWidth <= BREAKPOINTS.lg
            ? { topOffset: discountBanner ? 130 : 60 }
            : false
        }
        cardShadow
        screenIdentifier={screenIdentifier}
      />
    )
  }

  ReactDOM.render(<BreedSelectorComponent />, breedSelectorElement)
}

const initDogNameInputListener = (): void => {
  const input = document.querySelector('#dog-name-input') as HTMLInputElement
  const inputWrapper = document.querySelector('.wizard-entry__input')
  const wizardEntryCTA = document.querySelector('.wizard-entry__cta')
  const dogData = getPersistedDogData()

  if (!input || !inputWrapper || !wizardEntryCTA) {
    return
  }

  let hasInteracted = false

  if (dogData && dogData.length > 0 && dogData[0].name !== null) {
    inputWrapper.classList.add('stop-animation')
    input.setAttribute('value', dogData[0].name)
    wizardEntryCTA.classList.add('btn--prompt')
  }

  input.addEventListener('focus', (): void => {
    inputWrapper.classList.add('stop-animation')
  })

  input.addEventListener('focusout', (e: Event): void => {
    if (
      e.currentTarget instanceof window.HTMLInputElement &&
      !e.currentTarget.value
    ) {
      inputWrapper.classList.remove('stop-animation')
      wizardEntryCTA.classList.remove('btn--prompt')
    }
  })

  input.addEventListener('input', (e: Event): void => {
    if (e.currentTarget instanceof window.HTMLInputElement) {
      setPersistedDogData(getPersistedDogData(), e.currentTarget.value)
      if (e.currentTarget.value) {
        wizardEntryCTA.classList.add('btn--prompt')
      } else {
        wizardEntryCTA.classList.remove('btn--prompt')
      }
      if (!hasInteracted) {
        triggerDogNameInputAnalytics()
        hasInteracted = true
      }
    }
  })
}

const animateBowlSlider = (container: HTMLElement): void => {
  const bowl = document.getElementById('fresh-food__raw-bowl')
  if (!bowl) throw new Error('No fresh-food__raw-bowl found')
  const border = document.getElementById('fresh-food__meal-slider__border')
  if (!border) return

  const offset = 375
  const scrollPosition = window.scrollY - (container.offsetTop - offset)
  const scrollPercentage =
    Math.min(Math.max(scrollPosition / container.clientHeight, 0), 1) * 100
  if (scrollPercentage > 10 && scrollPercentage < 95) {
    // eslint-disable-next-line i18next/no-literal-string
    bowl.style.clipPath = `inset(0 ${scrollPercentage}% 0 0)`
    // We need webkitClipPath here so that this will replace the
    // scrollPercentage value for the meal slider to work successfully in Safari
    // 12.1 and older browsers
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: no any
    // eslint-disable-next-line i18next/no-literal-string
    bowl.style.webkitClipPath = `inset(0 ${scrollPercentage}% 0 0)`
    border.style.right = `${scrollPercentage}%`
    border.style.opacity = '1'
  } else {
    border.style.opacity = '0'
  }
}

const initBowlSliderAnimation = (): void => {
  const container = document.getElementById('fresh-food__meal-slider')
  if (!container) return
  const animateBowlSliderOnScroll = (): void => {
    window.requestAnimationFrame((): void => animateBowlSlider(container))
  }

  const handleScroll = (entries: Array<IntersectionObserverEntry>): void => {
    entries.forEach((entry: IntersectionObserverEntry): void => {
      if (entry.isIntersecting) {
        window.addEventListener('scroll', animateBowlSliderOnScroll)
      } else {
        window.removeEventListener('scroll', animateBowlSliderOnScroll)
      }
    })
  }

  const observer = new IntersectionObserver(handleScroll)
  observer.observe(container)
}

const initLearnMoreSection = (): void => {
  const learnMoreSectionElement = document.getElementById('learn-more-section')

  if (!learnMoreSectionElement) return

  ReactDOM.render(
    <LearnMoreSection
      tiles={['making-our-meals', 'our-products', 'how-it-works']}
    />,
    learnMoreSectionElement
  )
}

const initOurStoryVideo = () => {
  const ourStoryVideoContainer = document.querySelector('.our-story__video')

  if (!ourStoryVideoContainer) return

  const OurStoryVideoComponent = (): React.ReactElement<'video'> => {
    const { windowWidth } = useWindowSize()
    return (
      <Video
        loop
        playsInLine
        controls={false}
        alt="Kev and Dave, Butternut founders, enjoying a Butternut Box meal in the kitchen"
        muted
        slug={'kev-and-dave-eating-butternut'}
        playerHeight={windowWidth > BREAKPOINTS.md ? 780 : 320}
        playerWidth={windowWidth > BREAKPOINTS.md ? 780 : 320}
        video={{
          format: 'mp4'
        }}
      />
    )
  }

  ReactDOM.render(<OurStoryVideoComponent />, ourStoryVideoContainer)
}

const initOurProductsVideo = () => {
  const ourProductsVideoContainer = document.querySelector(
    '.our-products__video'
  )

  if (!ourProductsVideoContainer) {
    throw new Error('No ourProductsVideoContainer found')
  }

  const OurProductsVideoComponent = (): React.ReactElement<'video'> | null => {
    const { windowWidth } = useWindowSize()
    if (windowWidth <= BREAKPOINTS.lg) return null
    return (
      <Video
        loop
        playsInLine
        controls={false}
        alt="Sweet potatoes, green beans, meat, carrots, and broccoli dancing on pedestals"
        muted
        slug={'dancing-vegetables'}
        playerHeight={windowWidth > BREAKPOINTS.md ? 780 : 320}
        playerWidth={windowWidth > BREAKPOINTS.md ? 780 : 320}
        video={{
          format: 'mp4'
        }}
      />
    )
  }

  ReactDOM.render(<OurProductsVideoComponent />, ourProductsVideoContainer)
}

const initTabbedComponent = (): void => {
  const tabbedComponentElement = document.querySelector(
    '.our-products__tabbed-component'
  )
  if (!tabbedComponentElement)
    throw new Error('Could not find tabbedComponentElement')

  ReactDOM.render(
    <TabbedComponent
      namespace={namespace}
      showExtras={showExtras === 'true'}
    />,
    tabbedComponentElement
  )
}

const initInstagramCarousel = (): void => {
  const carouselContainer = document.querySelector('.social-proof__carousel')

  ReactDOM.render(
    <SocialReviewsSection countryCode={countryCode} />,
    carouselContainer
  )
}

const initStickyCTA = (): void => {
  const stickyCTA = document.getElementById('sticky-cta-wrapper')

  if (!stickyCTA) {
    return
  }

  const handleIntersect = (
    entries: Array<IntersectionObserverEntry>,
    observer: IntersectionObserver
  ): void => {
    entries.forEach((entry: IntersectionObserverEntry): void => {
      if (entry.isIntersecting) {
        stickyCTA.classList.add('transition-in')
        observer.unobserve(entry.target)
      }
    })
  }

  const observer = new IntersectionObserver(handleIntersect)
  const freshFoodClassName =
    isOg === 'true' ? 'fresh-food__content' : 'fresh-food-leaders'
  const freshFoodSection = document.getElementsByClassName(
    freshFoodClassName
  )[0] as HTMLElement

  if (!freshFoodSection) return
  observer.observe(freshFoodSection)
}

const initLanguage = (): void => {
  initI18n(preferredLanguage)
}

const initRoughCostCalculator = () => {
  const roughCostCalculatorElement = document.getElementById(
    'rough-cost-calculator'
  )

  // We return null for users who aren't in the variant to see the calculator
  if (!roughCostCalculatorElement) return null

  const RoughCostCalculatorComponent = () => {
    const copyContext = 'rough_cost_calculator'
    const baseClass = 'rough-cost-calculator__cost-calculator'

    const { windowWidth } = useWindowSize()
    const { t } = useTranslation(namespace)
    const roughCostCalculatorRef = useRef<HTMLDivElement>(null)
    const isDesktop = windowWidth > BREAKPOINTS.lg

    const onFocusCallback = useCallback(() => {
      if (!isDesktop && roughCostCalculatorRef.current) {
        const containerDistanceFromTop =
          roughCostCalculatorRef.current.getBoundingClientRect().top

        const y = containerDistanceFromTop + window.pageYOffset - 60

        // On iOS, when a user focuses on an input, the window is automatically
        // scrolled so the input element is placed above the keyboard. By setting a
        // very small timeout, we can ensure that our custom scroll position does not
        // scroll too far which was observed previously.
        setTimeout(() => window.scrollTo({ top: y, behavior: 'smooth' }), 100)
      }
    }, [isDesktop, roughCostCalculatorRef])

    return isOg === 'true' ? (
      <FullSectionRoughCostCalculator
        preferredLanguage={preferredLanguage}
        screenIdentifier="homepage"
        discountBasis={discountBasis as DiscountBasis}
        discountValue={parseInt(discountFirstPartValue || '') || 0}
        shippingCountryCode={shippingCountryCode as CountryCode}
        showImage
        isOg
      />
    ) : (
      <div className={`${baseClass}__alternative`}>
        <div className={`${baseClass}__card`}>
          <h2 className={`${isDesktop ? 'display-24' : 'display-20'} no-scale`}>
            {t(`${copyContext}.alternative_title`)}
          </h2>
          <DogDetailsForm
            breedSelectRef={roughCostCalculatorRef}
            onBreedFocusCallback={onFocusCallback}
          />
          <div className={`${baseClass}__plan-row-alternative`}>
            <Plan
              description={t(`${copyContext}.fresh_butternut`)}
              planType="bowl"
              copyContext={copyContext}
              namespace={namespace}
              preferredLanguage={preferredLanguage}
              discountBasis={discountBasis as DiscountBasis}
              discountValue={parseInt(discountFirstPartValue || '') || 0}
              shippingCountryCode={countryCode}
            />
          </div>
          <Cta
            namespace={namespace}
            text={`${copyContext}.build_your_box`}
            screenIdentifier={screenIdentifier}
          />
          {trustpilotBusinessUnitId && (
            <TrustpilotWidget
              businessUnitId={trustpilotBusinessUnitId}
              variant={Variant.Default}
              theme={Theme.Light}
              height="80"
              width="100%"
            />
          )}
        </div>
      </div>
    )
  }

  ReactDOM.render(<RoughCostCalculatorComponent />, roughCostCalculatorElement)
}

const initTestimonialsTrustpilot = (): void => {
  const container = document.querySelector('.testimonials__trustpilot')
  if (!container) return

  ReactDOM.render(
    <TestimonialsTrustpilot
      shippingCountryCode={countryCode}
      isPsibufet={isPsibufet === 'true'}
    />,
    container
  )
}
const initRealStories = (): void => {
  const container = document.querySelector('.real-stories__content')
  if (!container) return

  ReactDOM.render(
    <RealStoriesVideoCarousel preferredLanguage={preferredLanguage} />,
    container
  )
}

const initComparisonTable = (): void => {
  const container = document.querySelector('.comparison_table')
  if (!container) return

  ReactDOM.render(
    <ComparisonTable
      shippingCountryCode={countryCode}
      isPrewizardGuest={isPrewizardGuest === 'true'}
      screenIdentifier={screenIdentifier}
      isUserSignedIn={signedIn === 'true'}
    />,
    container
  )
}

document.addEventListener('DOMContentLoaded', (): void => {
  initLanguage()
  if (!window.matchMedia('(prefers-reduced-motion)').matches) {
    initBowlSliderAnimation()
  }
  initLearnMoreSection()
  initCookieConsent()
  initBreedSelector()
  initDogNameInputListener()
  if (countryCode !== 'DE' && isOg === 'true') {
    initInstagramCarousel()
  }
  if (isOg === 'true') {
    initOurProductsVideo()
    initTabbedComponent()
  }
  initStickyCTA()
  initGoogleClickIdentifierSession()
  initGeoIpWidget({
    desktopNav: true,
    mobileSidebar: true,
    contentTagElementId: 'paid-traffic-content-tag'
  })
  if (countryCode !== 'PL' && countryCode !== 'CZ') {
    initOurStoryVideo()
  }
  initRoughCostCalculator()
  playVideosOnScroll()
  initFooterLeadCapture()
  if (isOg !== 'true') {
    initTestimonialsTrustpilot()
  }
  !isDevelopment() && amplitudeApiKey && amplitude.init(amplitudeApiKey)
  if (phase3MultivariateVariation === 'variant_1') {
    initRealStories()
  }
  if (phase3MultivariateVariation === 'variant_2') {
    initComparisonTable()
  }
})
